import { useState } from "react";
import type { FormEvent } from "react";
import { subscribeBlock } from "@/mocks/homeData";

const FORM_ID = "newsletter-44-black";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dainavjp14a1h3mr77h0";

type Status = "idle" | "loading" | "success" | "error";

export default function SubscribeForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，稍后你会在邮箱里收到我们的第一封信。");
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      params.append(key, String(value));
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，稍后你会在邮箱里收到我们的第一封信。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试一次。");
      }
    } catch {
      setStatus("error");
      setMessage("网络好像有点问题，请检查连接后再试一次。");
    }
  };

  return (
    <section id="subscribe" className="w-full px-4 py-12 md:px-8 md:py-16">
      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-accent-50 p-6 md:p-9">
            <span className="font-label text-[11px] tracking-[0.24em] text-accent-800">
              {subscribeBlock.eyebrow}
            </span>
            <h3 className="mt-3 font-heading text-2xl font-black leading-9 tracking-tight text-foreground-950 md:text-3xl md:leading-[1.3]">
              {subscribeBlock.title}
            </h3>
            <p className="mt-4 max-w-md font-body text-sm leading-7 text-foreground-700">
              {subscribeBlock.desc}
            </p>
            <ul className="mt-6 space-y-3">
              {subscribeBlock.bullets.map((b) => (
                <li key={b} className="flex items-center gap-3 font-body text-sm text-foreground-800">
                  <i className="ri-checkbox-circle-fill text-base text-primary-500" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 md:p-9">
            <form
              id={FORM_ID}
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
              noValidate
            >
              <div>
                <label htmlFor="sub-email" className="font-label text-xs text-foreground-700">
                  邮箱地址
                </label>
                <input
                  id="sub-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div>
                <label htmlFor="sub-channel" className="font-label text-xs text-foreground-700">
                  想优先收到
                </label>
                <select
                  id="sub-channel"
                  name="channel"
                  defaultValue="今日黑料"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors duration-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="今日黑料">今日黑料</option>
                  <option value="实时爆料">实时爆料</option>
                  <option value="新料速递">新料速递</option>
                  <option value="深度特辑">深度特辑</option>
                </select>
              </div>

              <div>
                <label htmlFor="sub-frequency" className="font-label text-xs text-foreground-700">
                  推送频率
                </label>
                <select
                  id="sub-frequency"
                  name="frequency"
                  defaultValue="每日 2 封（早晚）"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors duration-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="每日 2 封（早晚）">每日 2 封（早晚）</option>
                  <option value="每日 1 封（晚间）">每日 1 封（晚间）</option>
                  <option value="仅重大事件">仅重大事件</option>
                </select>
              </div>

              <input
                className="field-ghost"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-1 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <i className={status === "loading" ? "ri-loader-4-line animate-spin text-base" : "ri-mail-send-line text-base"} />
                {status === "loading" ? "提交中…" : "订阅速递"}
              </button>

              {status === "success" && (
                <p className="flex items-start gap-2 rounded-md bg-accent-100 px-3.5 py-2.5 font-body text-[13px] leading-6 text-accent-900">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-base" />
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="flex items-start gap-2 rounded-md bg-primary-100 px-3.5 py-2.5 font-body text-[13px] leading-6 text-primary-800">
                  <i className="ri-error-warning-fill mt-0.5 text-base" />
                  {message}
                </p>
              )}

              <p className="font-label text-xs leading-5 text-secondary-600">
                订阅即表示你同意我们仅使用你的邮箱发送速递内容，我们不会与其他方共享。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}